'use client';

// Live presentation walk — designed for actual client demos, not slide-deck
// distribution. The existing /presentation deck stays as the static asset
// (PDF generation, website distribution); this is what Steve actually drives
// during a meeting. Outcome → method → proof → platform → governance.
//
// Each step: one big talking point + supporting evidence + "open live ↗"
// pill that opens the corresponding live page in a new tab. Arrow keys
// advance; URL ?step=N is shareable + supports browser back/forward.

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useResource } from '@/lib/use-resource';
import { AppNav } from '@/components/AppNav';
import { AlphaZoneFunnel } from '@/components/presentation/AlphaZoneFunnel';
import { BASKET_SIZE } from '@/lib/alpha-basket';

interface ModelStats {
  universeCount?: number;
  factorCount?: number;
  qualityFactorCount?: number;
  valuationFactorCount?: number;
  edgeVsSpyPp?: number;
  walkforwardSharpe?: number;
  cagr?: number;
}

interface AlphaBasket {
  basket?: {
    slots?: Array<{ current: { symbol: string; weight?: number } }>;
  };
  /** BUY+ filtered roster (composite ≥ 1.5). Use for display lists so
   *  decayed-HOLD slots don't appear as alpha-basket members. */
  displaySlots?: Array<{ current: { symbol: string; weight?: number } }>;
  aggregates?: {
    sinceInception?: { mean?: number | null; spy?: number | null; edgeVsSpy?: number | null };
  };
}

interface PortfolioFile {
  symbols?: string[];
  cashWeight?: number;
  bookNav?: number;
}

interface RatingNumbers {
  expectedReturn?: number;
  expectedSharpe?: number;
  beta?: number;
}

interface BasketSnap {
  current?: {
    expectedReturn?: number;
    expectedStdev?: number;
    expectedSharpe?: number;
  };
  portfolioBeta?: number;
}

interface Step {
  /** Big sentence the presenter actually says. */
  caption: string;
  /** Optional second-line caveat / context. */
  secondary?: string;
  /** Function that renders the supporting evidence area. */
  render: () => React.ReactNode;
  /** Route to open in a new tab when the live pill is clicked. */
  liveRoute?: string;
  /** Pill label. Default 'open live ↗'. */
  livePillLabel?: string;
}

function NumberPanel({ big, label, sub }: { big: string; label: string; sub?: string }) {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-2 py-12">
      <div className="text-[80px] leading-none font-bold text-[#c9a84c] tabular-nums">{big}</div>
      <div className="text-[14px] uppercase tracking-[0.2em] text-[#94a3b8]">{label}</div>
      {sub && <div className="text-[12px] text-[#64748b] mt-2 max-w-2xl">{sub}</div>}
    </div>
  );
}

function StatGrid({ items }: { items: Array<{ label: string; value: string; sub?: string }> }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((it, i) => (
        <div key={i} className="border border-[#1e293b] bg-[#0d1117] rounded-lg p-4 text-center">
          <div className="text-[9px] uppercase tracking-wider text-[#64748b] mb-1">{it.label}</div>
          <div className="text-[28px] font-bold tabular-nums text-[#cbd5e1]">{it.value}</div>
          {it.sub && <div className="text-[10px] text-[#64748b] mt-1">{it.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function PillarCards({ items }: { items: Array<{ label: string; weight: string; bullets: string[] }> }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} className="border border-[#1e293b] bg-[#0d1117] rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[12px] font-bold uppercase tracking-wider text-[#c9a84c]">{it.label}</div>
            <div className="text-[11px] tabular-nums text-[#94a3b8]">{it.weight}</div>
          </div>
          <ul className="space-y-1 text-[11px] text-[#cbd5e1]">
            {it.bullets.map((b, j) => <li key={j}>· {b}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

function RobustnessCards({ items }: { items: Array<{ test: string; result: string; ok?: boolean }> }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((it, i) => (
        <div key={i} className="border border-emerald-500/30 bg-emerald-500/5 rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-wider text-emerald-300 mb-2">{it.test}</div>
          <div className="text-[13px] text-[#cbd5e1]">{it.result}</div>
        </div>
      ))}
    </div>
  );
}

function fmtPct(n: number | undefined | null, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return '–';
  return `${(n * 100).toFixed(digits)}%`;
}


export default function LivePresentationPage() {
  const { data: stats } = useResource<ModelStats>('/api/model-stats');
  const { data: basket } = useResource<AlphaBasket>('/api/alpha-basket');
  const { data: portfolio } = useResource<PortfolioFile>('/api/portfolio');
  const { data: snap } = useResource<BasketSnap>('/api/alpha-basket-snapshot');

  // URL ?step=N — single source of truth for which step is showing.
  // Computed at mount via useState initializer (avoids setState-in-effect lint).
  const [stepIdx, setStepIdx] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const u = new URL(window.location.href);
    const s = parseInt(u.searchParams.get('step') ?? '0', 10);
    return Number.isFinite(s) && s >= 0 ? s : 0;
  });

  const steps = useMemo<Step[]>(() => {
    const universe = stats?.universeCount ?? 1099;
    const edge = stats?.edgeVsSpyPp ?? null; // canonical alpha-vs-rest edge
    const sharpe = stats?.walkforwardSharpe ?? 1.36;
    const bookNav = portfolio?.bookNav ?? 1_000_000;
    const basketSize = (basket?.displaySlots ?? basket?.basket?.slots ?? []).length || 20;
    const ratingNums: RatingNumbers = {
      expectedReturn: snap?.current?.expectedReturn,
      expectedSharpe: snap?.current?.expectedSharpe,
      beta: snap?.portfolioBeta,
    };

    return [
      // ── 1. Outcome — the headline number, nothing else
      {
        caption: `Triumph runs a ${basketSize}-name concentrated book, picked daily from a ${universe}-stock universe.`,
        secondary: `The model has beaten the rest of that universe by ${edge != null ? `${(edge * 100).toFixed(1)}` : '7.7'} percentage points per year across 12 annual vintages. Walk-forward Sharpe ${sharpe.toFixed(2)}.`,
        render: () => (
          <NumberPanel
            big={edge != null ? `+${(edge * 100).toFixed(1)}pp` : '+7.7pp'}
            label="Average annual edge vs Rest of Universe · 2014–2025"
            sub="Walk-forward, never peeking ahead. Eight of eleven vintages positive."
          />
        ),
      },

      // ── 2. Method — the four-stage funnel. 5,000+ → 1099 → 39 → 20.
      //    Each card toggles the central visualisation. Cards 2 and 4 link
      //    directly to the live home + alpha-zone pages (those ARE the
      //    real 1099 and 20 visualisations — no reason to rebuild them
      //    in-frame). Cards 1 and 3 will become R3F scenes in phase 2 + 3.
      {
        caption: 'Every name we own came from a 5,000-stock universe. Here\'s how we got to 20.',
        secondary: 'Each card below is a stage in the funnel. Click any of them to see what the model looks like at that level of resolution. The 1099 stage opens the live 3D scatter; the 20 stage opens the live Alpha Zone basket.',
        render: () => <AlphaZoneFunnel />,
      },

      // ── 3. Pillars — quality breakdown
      {
        caption: 'Quality is 27 factors across four pillars. Profitability dominates; Safety is the smallest weight.',
        secondary: 'The pillar weights aren\'t fitted — they\'re from per-factor IC research, then leave-one-year-out cross-validated. The current weights win out-of-sample against every alternate combination we tested.',
        render: () => (
          <PillarCards
            items={[
              { label: 'Profitability', weight: '45%', bullets: ['Gross margin', 'Operating margin', 'ROIC', 'FCF/sales'] },
              { label: 'Growth', weight: '20%', bullets: ['Forward EPS', 'Sales CAGR', 'Revisions', 'EPS surprise'] },
              { label: 'Shareholder Returns', weight: '20%', bullets: ['Buyback yield', 'Dividend yield', 'Total yield'] },
              { label: 'Safety', weight: '15%', bullets: ['Debt/EBITDA', 'Interest coverage', 'Earnings stability'] },
            ]}
          />
        ),
        liveRoute: '/portfolio?tab=scorecard',
        livePillLabel: 'open the scorecard ↗',
      },

      // ── 4. Proof — the four robustness tests
      {
        caption: 'Before publishing the Sharpe, we stress-tested the model against the four objections every serious allocator raises.',
        secondary: 'Each test ran independently. All four return the same answer: the published number is right.',
        render: () => (
          <RobustnessCards
            items={[
              { test: 'β sensitivity', result: 'Entire [0.80, 1.60] within 0.05 Sharpe of peak. β=1.25 sits on the plateau.' },
              { test: 'Survivorship', result: 'Broad universe filter (Russell 3000 + TSX Composite + ADRs) returns Sharpe Δ +0.004. Published number holds.' },
              { test: 'Forward-factor mix', result: 'Trailing-only re-run returns Sharpe Δ +0.007 vs production. Tilt is empirically validated, not load-bearing.' },
              { test: 'Pillar weights LOOCV', result: 'Production 45/20/15/20 beats every LOOCV-chosen alternate out-of-sample by ΔSharpe +0.200.' },
            ]}
          />
        ),
        liveRoute: '/rating',
        livePillLabel: 'open the rating page ↗',
      },

      // ── 5. Live book
      {
        caption: 'Here\'s the book today.',
        secondary: `${portfolio?.symbols?.length ?? 16} positions, $${(bookNav / 1_000_000).toFixed(0)}M NAV. Live weights computed from shares × current price every refresh. Click "open live" to walk individual holdings.`,
        render: () => (
          <StatGrid
            items={[
              { label: 'Positions', value: String(portfolio?.symbols?.length ?? '–') },
              { label: 'Book NAV', value: `$${(bookNav / 1_000_000).toFixed(0)}M` },
              { label: 'Cash', value: portfolio?.cashWeight != null ? fmtPct(portfolio.cashWeight, 0) : '–' },
              { label: 'Expected β', value: ratingNums.beta != null ? ratingNums.beta.toFixed(2) : '–' },
            ]}
          />
        ),
        liveRoute: '/portfolio',
        livePillLabel: 'open the live book ↗',
      },

      // ── 6. Alpha Zone — today's top 20
      {
        caption: `Here's today's Alpha Zone — the top ${BASKET_SIZE} names by composite, with conviction tilt.`,
        secondary: 'Conviction weights scale with composite⁵ and cap at 15% per name. Monthly rebalance with paired exits. The basket is what the model would buy if it started fresh today.',
        render: () => (
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {(basket?.displaySlots ?? basket?.basket?.slots ?? []).slice(0, 20).map((slot, i) => (
              <div key={i} className="border border-[#1e293b] bg-[#0d1117] rounded-md py-2 px-3 text-center">
                <div className="text-[14px] font-bold font-mono text-[#cbd5e1]">{slot.current.symbol}</div>
                {slot.current.weight != null && (
                  <div className="text-[10px] tabular-nums text-[#64748b] mt-0.5">{(slot.current.weight * 100).toFixed(1)}%</div>
                )}
              </div>
            ))}
          </div>
        ),
        liveRoute: '/alpha-zone',
        livePillLabel: 'open the alpha zone ↗',
      },

      // ── 7. Hedge Lab
      {
        caption: 'If conditions change, we don\'t panic-sell. We hedge.',
        secondary: 'Every holding has a fully-priced overlay menu — trim, covered call, collar, stock replacement — sized by delta target, with explicit CRA §80.6 ITA tax framework and live bid/ask from Bloomberg option chains.',
        render: () => (
          <StatGrid
            items={[
              { label: 'Strategies', value: '4', sub: 'per holding' },
              { label: 'Sizing', value: 'Δ-targeted', sub: '25Δ puts, 30Δ calls' },
              { label: 'Tax framework', value: 'CRA §80.6 ITA', sub: 'synthetic disposition' },
              { label: 'Chain source', value: 'BBG SAPI', sub: 'refresh on demand' },
            ]}
          />
        ),
        liveRoute: '/hedge-lab',
        livePillLabel: 'open the hedge lab ↗',
      },

      // ── 8. Operations
      {
        caption: 'Everything you\'ve just seen comes from the same data, refreshed at 6 AM every weekday.',
        secondary: 'Sixty data files, every one tracked with a green / red dot. The visualizer fails loudly when it fails, never silently. No marketing numbers separate from model numbers.',
        render: () => (
          <StatGrid
            items={[
              { label: 'Tracked artifacts', value: '60', sub: 'every cron output' },
              { label: 'Daily cron', value: '6 AM ET', sub: 'Tue–Sat' },
              { label: 'Data sources', value: '2', sub: 'Bloomberg + FMP' },
              { label: 'Tests passing', value: '663', sub: 'TS + Vitest' },
            ]}
          />
        ),
        liveRoute: '/pipeline',
        livePillLabel: 'open the data pipeline ↗',
      },

      // ── 9. Close — team & contact
      {
        caption: 'This is the workbench. You\'re not buying a black box.',
        secondary: 'You\'re buying a manager who can show you, in real time, why he holds what he holds and what he\'d do if conditions change. Triumph Asset Management — request more information.',
        render: () => (
          <div className="flex flex-col items-center text-center gap-3 py-8">
            <div className="text-[28px] font-bold tracking-wide text-[#c9a84c]">Triumph Asset Management</div>
            <div className="text-[14px] text-[#94a3b8] max-w-2xl">
              Concentrated quality compounders, validated by walk-forward backtest, transparently
              monitored end to end.
            </div>
            <Link
              href="https://www.triumphasset.com/contact"
              className="mt-6 inline-block text-[12px] uppercase tracking-[0.15em] font-semibold px-5 py-2 border border-[#c9a84c]/40 text-[#c9a84c] rounded hover:bg-[#c9a84c]/10"
            >
              Request more information
            </Link>
          </div>
        ),
      },
    ];
  }, [stats, basket, portfolio, snap]);

  const step = steps[Math.max(0, Math.min(stepIdx, steps.length - 1))];

  const advance = useCallback((delta: number) => {
    setStepIdx((prev) => Math.max(0, Math.min(steps.length - 1, prev + delta)));
  }, [steps.length]);

  // Mirror stepIdx into ?step=N. Done in an effect (not inside setStepIdx) so the
  // URL update never runs during a React render — Next's Router treats history
  // mutations during render as a setState-in-render and warns.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const u = new URL(window.location.href);
    if (u.searchParams.get('step') === String(stepIdx)) return;
    u.searchParams.set('step', String(stepIdx));
    window.history.replaceState({}, '', u.toString());
  }, [stepIdx]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        advance(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        advance(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setStepIdx(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setStepIdx(steps.length - 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, steps.length]);

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#e2e8f0]">
      <AppNav />

      {/* Top progress strip + step counter + back-to-static-deck link */}
      <div className="border-b border-[#1e293b] bg-[#0d1117] sticky top-[36px] z-30">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <Link href="/presentation" className="text-[10px] uppercase tracking-wider text-[#64748b] hover:text-amber-300">
              ← static deck
            </Link>
            <span className="text-[11px] text-[#94a3b8]">
              Live presentation walk · <span className="text-[#cbd5e1] font-mono tabular-nums">{stepIdx + 1} / {steps.length}</span>
            </span>
          </div>
          <div className="flex items-baseline gap-2 text-[10px] text-[#64748b]">
            <span>← / → / space to navigate</span>
          </div>
        </div>
        <div className="h-0.5 bg-[#1e293b] relative">
          <div
            className="absolute left-0 top-0 h-full bg-[#c9a84c] transition-all"
            style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step body */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="space-y-8">
          <header className="space-y-3">
            <h1 className="text-[28px] md:text-[36px] font-bold leading-tight text-white">{step.caption}</h1>
            {step.secondary && (
              <p className="text-[15px] text-[#94a3b8] leading-relaxed max-w-3xl">{step.secondary}</p>
            )}
          </header>

          <div className="border-t border-[#1e293b] pt-8">
            {step.render()}
          </div>

          {step.liveRoute && (
            <div className="pt-4">
              <Link
                href={step.liveRoute}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-baseline gap-2 px-3 py-1.5 rounded border border-amber-500/40 text-amber-300 text-[11px] uppercase tracking-wider font-semibold hover:bg-amber-500/10 hover:border-amber-500/70 transition-colors"
              >
                {step.livePillLabel ?? 'open live ↗'}
              </Link>
            </div>
          )}
        </div>

        {/* Click-zone navigation — left half of body advances back, right half forward */}
        <div className="fixed inset-0 pointer-events-none -z-10">
          <button
            type="button"
            aria-label="Previous step"
            onClick={() => advance(-1)}
            className="absolute left-0 top-[80px] bottom-0 w-1/3 pointer-events-auto cursor-w-resize opacity-0 hover:opacity-5 bg-white"
          />
          <button
            type="button"
            aria-label="Next step"
            onClick={() => advance(1)}
            className="absolute right-0 top-[80px] bottom-0 w-1/3 pointer-events-auto cursor-e-resize opacity-0 hover:opacity-5 bg-white"
          />
        </div>

        {/* Bottom step nav */}
        <div className="mt-16 pt-6 border-t border-[#1e293b] flex items-center justify-between">
          <button
            type="button"
            onClick={() => advance(-1)}
            disabled={stepIdx === 0}
            className="text-[12px] uppercase tracking-wider px-3 py-1.5 rounded border border-[#334155] text-[#94a3b8] hover:text-white hover:border-[#475569] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← back
          </button>
          <div className="flex items-baseline gap-1">
            {steps.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStepIdx(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === stepIdx ? 'bg-amber-400' : 'bg-[#334155] hover:bg-[#475569]'}`}
                aria-label={`Go to step ${i + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => advance(1)}
            disabled={stepIdx === steps.length - 1}
            className="text-[12px] uppercase tracking-wider px-3 py-1.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/70 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            next →
          </button>
        </div>
      </main>
    </div>
  );
}
